/**
 * MUTAGIM.SHOP — Public Wishlist Page (Phase 9)
 * Page de wishlist partagée (accessible sans connexion)
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, ShoppingCart, ExternalLink } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PublicWishlistPage() {
  const { shareToken } = useParams();
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const [wishlistData, setWishlistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPublicWishlist();
  }, [shareToken]);

  const fetchPublicWishlist = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/wishlist/public/${shareToken}`);
      
      if (response.ok) {
        const data = await response.json();
        setWishlistData(data);
      } else if (response.status === 404) {
        setError('Cette liste de favoris n\'existe pas ou est privée.');
      } else {
        setError('Erreur lors du chargement de la liste.');
      }
    } catch (err) {
      console.error('Error fetching public wishlist:', err);
      setError('Erreur de connexion.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product) => {
    await addToCart(product.id);
    toast.success(`${product.name[i18n.language] || product.name.fr} ajouté au panier`);
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-mutagim-dark flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-mutagim-red border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Chargement de la liste...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Erreur
  if (error) {
    return (
      <div className="min-h-screen bg-mutagim-dark flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md p-8">
            <Heart className="w-16 h-16 mx-auto mb-4 text-gray-700" />
            <h1 className="text-xl font-semibold text-white mb-2">Liste introuvable</h1>
            <p className="text-gray-400 mb-6">{error}</p>
            <Link to="/">
              <Button className="bg-mutagim-red hover:bg-red-700">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mutagim-dark flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Heart className="w-12 h-12 mx-auto mb-3 text-mutagim-red fill-mutagim-red" />
          <h1 className="text-3xl font-bold text-white mb-2">
            Liste de favoris de {wishlistData?.owner_name}
          </h1>
          <p className="text-gray-400">
            {wishlistData?.count} produit{wishlistData?.count > 1 ? 's' : ''} dans cette liste
          </p>
        </div>

        {/* Liste vide */}
        {wishlistData?.items?.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400">Cette liste est vide pour le moment.</p>
          </div>
        )}

        {/* Produits */}
        {wishlistData?.items?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {wishlistData.items.map((item) => {
              const product = item.product;
              if (!product) return null;

              const name = product.name?.[i18n.language] || product.name?.fr || 'Produit';
              const image = Array.isArray(product.images) 
                ? (product.images[0] || '/placeholder-product.jpg')
                : (product.images?.main || '/placeholder-product.jpg');
              const isOutOfStock = product.stock === 0;
              const badge = product.badges?.primary_badge;

              return (
                <div 
                  key={item.id}
                  className="bg-gray-900/80 rounded-lg overflow-hidden border border-gray-800 hover:border-mutagim-red/50 transition-all"
                >
                  {/* Image */}
                  <Link to={`/product/${product.slug}`} className="block relative aspect-square">
                    <img 
                      src={image} 
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                    {badge && (
                      <span className={cn(
                        "absolute top-2 start-2 px-2 py-1 text-xs font-bold text-white rounded-md",
                        badge === 'new' && 'bg-green-500',
                        badge === 'trending' && 'bg-orange-500',
                        badge === 'almost_sold_out' && 'bg-red-600',
                        badge === 'mutagim_pick' && 'bg-purple-500'
                      )}>
                        {badge === 'new' && 'NOUVEAU'}
                        {badge === 'trending' && 'TENDANCE'}
                        {badge === 'almost_sold_out' && 'PRESQUE ÉPUISÉ'}
                        {badge === 'mutagim_pick' && 'SÉLECTION'}
                      </span>
                    )}
                    {isOutOfStock && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span className="text-white font-semibold">Rupture de stock</span>
                      </div>
                    )}
                  </Link>

                  {/* Infos */}
                  <div className="p-4">
                    <Link to={`/product/${product.slug}`}>
                      <h3 className="text-white font-medium hover:text-mutagim-red transition-colors line-clamp-2">
                        {name}
                      </h3>
                    </Link>
                    
                    {product.stats?.average_rating > 0 && (
                      <div className="flex items-center gap-1 mt-1 text-sm text-gray-400">
                        <span className="text-yellow-400">★</span>
                        <span>{product.stats.average_rating.toFixed(1)}</span>
                        <span>({product.stats.reviews_count})</span>
                      </div>
                    )}

                    <p className="text-lg font-bold text-mutagim-red mt-2">
                      {formatPrice(product.pricing?.prices)}
                    </p>

                    <div className="flex gap-2 mt-4">
                      <Button 
                        onClick={() => handleAddToCart(product)}
                        disabled={isOutOfStock}
                        className="flex-1 bg-mutagim-red hover:bg-red-700"
                        size="sm"
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Ajouter
                      </Button>
                      <Link to={`/product/${product.slug}`}>
                        <Button 
                          variant="outline"
                          size="icon"
                          className="border-gray-700 text-gray-400 hover:text-white"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-12 p-8 bg-gray-900/50 rounded-lg border border-gray-800">
          <h2 className="text-xl font-semibold text-white mb-2">
            Vous aimez cette sélection ?
          </h2>
          <p className="text-gray-400 mb-4">
            Créez votre propre liste de favoris sur Mutagim.shop
          </p>
          <Link to="/">
            <Button className="bg-mutagim-red hover:bg-red-700">
              Découvrir nos produits
            </Button>
          </Link>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default PublicWishlistPage;
